/* eslint-disable react/jsx-props-no-spreading */
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { F, is, isEmpty, isNil } from 'ramda';
import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createGlobalState, useFullscreen, useLockBodyScroll, useToggle } from 'react-use';

import { PlaybackMeta } from '@skytvnz/sky-app-store/lib/types/models/PlaybackMeta';

import { YouboraPlayerConfig } from '@/Components/VideoPlayer/Core/YouboraPlayerConfig';
import { VIEWING_METHODS } from '@/Hooks/PUJ/constants';
import usePackageOfferDetails from '@/Hooks/PUJ/usePackageOfferDetails';
import usePersistCallback from '@/Hooks/usePersistCallback';
import usePujFlag from '@/Hooks/usePujFlag';
import { Flags, useFeatureFlag } from '@/Hooks/useFeatureFlags';
import { useSubscriptionModal } from '@/Hooks/useSubscription';
import AspectRatioBox, { AspectRatio } from '@/Layouts/containers/AspectRatioBox';
import { actions } from '@/Store';
import AppEventBus, { AppEvent } from '@/Utils/AppEventBus';
import { getDeviceId } from '@/Utils/Device';
import { numberIn0To1 } from '@/Utils/NumberInRange';
import { PLAYER_MODE, getViewPage } from '@/Utils/PageLocation';
import navigateToPackage, { getOfferIdFromViewingMethod } from '@/Utils/Puj';
import { useHistory, useLocation } from 'react-router-dom';
import { getAdTotalDurationBehindTime, SsaiEventType, updateCuePoints } from '@/Utils/SSAI';
import {
  AdEvents,
  AdPayloadProps,
  AdPodPayloadProps,
  Reasons,
  VideoContentPayloadProps,
} from '@/Analytics/SsaiTracking';
import { IS_SAFARI } from '@/Utils/Browser';
import ControlPanel from './ControlPanel';
import BrightCovePlayer, { POSTER_CLASSNAME } from './Core/BrightCovePlayer';
import { PlayerController, SsaiAdPodProps, SsaiAdBreakProps } from './Core/PlayerTypes';
import ShakaPlayer from './Core/ShakaPlayer';
import SkipIntroPanel from './SkipIntroAutoplayPanel';
import { DrawerCommonProps, DrawerContentItem, MediaInfo } from './VideoTypes';

import useSimpleModal from '../Modal/useSimpleModal';
import {
  destroyMediaTailorSession,
  setDigitalAdControlsEnabled,
} from './Core/DataZoomMediaTailorConfig';
import styles from './styles.module.scss';
import AdCountdownPanel from './AdCountdownPanel';
import { MAX_STROKE_DASH_OFFSET } from '../Icons/AdCountdownProgressIcon';

export const PLAYER_USER_INACTIVE_TIMEOUT = 5000; // in ms
export const PLAYER_DURATION_JUMP = 10; // in seconds
export const PLAYER_VOLUME_DEFAULT = 1;
export const PLAYER_VOLUME_UNMUTE = 0.05;
export const PLAYER_VOLUME_JUMP = 0.05;

const useGlobalVolume = createGlobalState<number>(PLAYER_VOLUME_DEFAULT);
const useGlobalMute = createGlobalState<boolean>(false);

const SSAI_SEEK_ZERO_WORKAROUND_TIME = 0.5;
const HIDE_BC_POSTER_DELAY = 1500; // in ms

// Props can be used by outside
export interface VideoProps {
  className?: string;
  startTime?: number;
  autoPlay?: boolean;
  fullWindow?: boolean;
  lockPageScrollOnFullWindow?: boolean;
  exitFullWindowOnPlayEnd?: boolean;
  pauseOnExitFullWindow?: boolean;
  onExitFullWindow?: () => void | boolean;
}

// Props only can be used by Live and Vod player
// TODO: do not pass the `player` instance to outside.
// Define a data interface and pass the pure data to outside to consume.
interface Props extends VideoProps, Partial<DrawerCommonProps> {
  mediaInfo: MediaInfo;
  isDASH?: boolean;
  playbackMeta?: PlaybackMeta;
  isLivePlayer?: boolean;
  isShowResourceButton?: boolean;
  isConcurrencyLimitReached?: boolean;
  assetId?: string;
  isSport?: boolean;
  onPlayerLoad?: () => void;
  onPlaybackDataLoad?: () => void;
  onVideoFirstPlay?: (player: PlayerController, adPayload: VideoContentPayloadProps) => void;
  onVideoPlay?: (player: PlayerController) => void;
  onVideoPlaying?: (player: PlayerController) => void;
  onVideoPause?: (player: PlayerController) => void;
  onVideoResume?: (player: PlayerController) => void;
  onVideoEnd?: (player: PlayerController) => void;
  onTimeUpdate?: (player: PlayerController) => void;
  onLiveEndUpdate?: (player: PlayerController) => void;
  onPlaybackPositionChange: (player: PlayerController) => void;
  youboraPlayerConfiguration?: YouboraPlayerConfig;
  playerReferrer?: string;
  playerMode?: string;
  watchFromStart?: boolean;
  brandId?: string;
  episodeId?: string;
  onAutoPlayNextEpisode: (episodeId: string) => void;
  watchDurationRef: React.MutableRefObject<number>;
  onStopPlayback: (player?: PlayerController, isBeforeUnload?: boolean) => void;
  onTrackSsaiAds?: (
    eventType: AdEvents,
    adPodPayload: AdPodPayloadProps,
    adPayload: AdPayloadProps,
  ) => void;
}

const VideoPlayer: FC<Props> = props => {
  const {
    // Player props for outside
    className,
    startTime,
    autoPlay = false,
    fullWindow = false,
    lockPageScrollOnFullWindow = false,
    exitFullWindowOnPlayEnd = false,
    pauseOnExitFullWindow = false,
    onExitFullWindow,

    // Player props for inside
    mediaInfo,
    playbackMeta,
    isLivePlayer = false,
    isShowResourceButton = false,
    isSport = false,
    isConcurrencyLimitReached = false,

    // Drawer props for inside
    drawerTabs,
    drawerContentList,
    isDrawerContentLoading,
    selectedTabId,
    selectedContentId,
    onDrawerTabChange,
    onDrawerContentSelect,
    onDrawerOpen,

    // Player events for inside
    onPlayerLoad,
    onVideoPlaying,
    onPlaybackDataLoad,
    onVideoFirstPlay,
    onVideoPlay,
    onVideoPause,
    onVideoEnd,
    onTimeUpdate,
    onPlaybackPositionChange,
    youboraPlayerConfiguration,
    playerReferrer,
    playerMode,
    watchFromStart = false,

    // Skip Intro Autoplay Props
    brandId,
    episodeId,
    onAutoPlayNextEpisode,
    isDASH,
    watchDurationRef,
    onStopPlayback,

    // SSAI
    onTrackSsaiAds,
  } = props;
  const shouldUseShakaPlayer = isDASH && isLivePlayer;
  const dispatch = useDispatch();
  const location = useLocation();
  const { openModal } = useSimpleModal();
  const { getEpisodeViewingMethods, getLinearChannelViewingMethods } = usePackageOfferDetails();
  const playbackMetaRef = useRef<PlaybackMeta>();
  const autoPlayRef = useRef<boolean>();
  const hasFirstPlayedRef = useRef<boolean>(false);
  const hasFirstTimeUpdatedRef = useRef<boolean>(false);
  const lastSeekableEndRef = useRef<number>();
  const hasVideoContentStartedFiredRef = useRef(false);

  // For controlling fullscreen
  const videoPlayerContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isFullWindowRef = useRef(false);

  // SSAI
  const ssaiAdPodCollectionRef = useRef<SsaiAdPodProps[]>([]);
  const ssaiAdPodIndexRef = useRef(-1);
  const ssaiAdPodPlayingRef = useRef<SsaiAdPodProps>();
  const ssaiAdPlayingRef = useRef<SsaiAdBreakProps>();
  const ssaiStrokeDashOffsetStepRef = useRef(0);
  const [adCountdown, setAdCountdown] = useState(0);

  //  Handling of resume points so first frame is not displayed
  const posterVisible = useRef(true);

  // For initializing player core
  const playerBaseRef = useRef<any>();
  const playerRef = useRef<PlayerController>();
  const hasPlayerPlaybackDataLoaded = useRef<boolean>(false);

  // player core state
  const [isLive, setIsLive] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useGlobalMute();
  const [volume, setVolume] = useGlobalVolume();
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [bufferRanges, setBufferRanges] = useState<TimeRanges>();
  const [isSeeking, setIsSeeking] = useState(false);
  const [hasAnimationEnded, setAnimationStatus] = useState(true);
  const [hasVideoEnded, setHasVideoEnded] = useState(false);
  const [isAdPlaying, setIsAdPlaying] = useState(false);
  // skipIntro AutoPlay state
  const [showAutoPlay, setShowAutoPlay] = useState<boolean>(false);
  const [hasAutoPlayOnEnd, setHasAutoPlayOnEnd] = useState(false);
  const hasAutoPlayOnEndRef = useRef(hasAutoPlayOnEnd);
  const hasAnimationEndedRef = useRef(hasAnimationEnded);
  useEffect(() => {
    hasAutoPlayOnEndRef.current = hasAutoPlayOnEnd;
    hasAnimationEndedRef.current = hasAnimationEnded;
  }, [hasAutoPlayOnEnd, hasAnimationEnded]);

  // watch duration
  const previousPlayerTime = useRef(0);
  const THROTTLE_RECORD_WATCH_TIME = 750; // in ms

  const { isPUJEnabled } = usePujFlag();
  const { isOn: isDigitalAdControlsEnabled } = useFeatureFlag(Flags.ENABLE_DIGITAL_AG_CONTROLS);

  useEffect(() => {
    setDigitalAdControlsEnabled(Boolean(isDigitalAdControlsEnabled));
  }, [isDigitalAdControlsEnabled]);

  // player container state
  const [isFullWindow, setIsFullWindow] = useState(fullWindow);
  const [shouldBodyScrollLock, setShouldBodyScrollLock] = useState(
    lockPageScrollOnFullWindow && fullWindow,
  );
  useLockBodyScroll(lockPageScrollOnFullWindow && shouldBodyScrollLock);

  const [isFullScreen, toggleIsFullScreen] = useToggle(false);
  useFullscreen(videoPlayerContainerRef, isFullScreen, {
    video: videoRef,
    onClose: () => {
      toggleIsFullScreen(false);
    },
  });
  const assetId = isLivePlayer ? playbackMeta?.id : playbackMeta?.mediaAssetId;
  const [isInteracting, toggleIsInteracting] = useToggle(false);
  const debounceToggleIsInteracting = useCallback(
    debounce(toggleIsInteracting, PLAYER_USER_INACTIVE_TIMEOUT),
    [],
  );
  useEffect(() => {
    return () => {
      debounceToggleIsInteracting.cancel();
    };
  }, [debounceToggleIsInteracting]);

  const trackSsaiAdInteraction = usePersistCallback((adEventType: AdEvents) => {
    const player = playerRef.current;
    if (!player) return;
    if (player.isSsaiAdPlaying() && ssaiAdPodPlayingRef.current && ssaiAdPlayingRef.current) {
      const adPodPayload = {
        ad_block_id: ssaiAdPodPlayingRef.current.podId,
        total_ads_in_block: ssaiAdPodPlayingRef.current.adBreaks.length,
        total_block_duration: ssaiAdPodPlayingRef.current.duration,
        action_adblock_timestamp: Math.round(player.absoluteTime()),
      };
      const adPayload = {
        adId: ssaiAdPlayingRef.current.adId,
        duration: ssaiAdPlayingRef.current.duration,
        action_timestamp: Math.round(player.currentTime()),
      };
      onTrackSsaiAds?.(adEventType, adPodPayload, adPayload);
    }
  });

  const stopPlayback = usePersistCallback((isBeforeUnload = false) => {
    const id = isLivePlayer ? playbackMeta?.id : playbackMeta?.mediaAssetId;
    // Only stopPlayback once playbackMeta is loaded
    if (id) {
      dispatch(actions.playback.stopPlayback(id, isLivePlayer, getDeviceId(), true));
      if (isLivePlayer) onStopPlayback();

      const player = playerRef.current;
      if (!player || !isBeforeUnload) return;
      if (player.isSsaiAdPlaying()) trackSsaiAdInteraction(AdEvents.AdExited);
      else onStopPlayback(player, isBeforeUnload);
    }
  });

  const unloadListener = usePersistCallback(() => stopPlayback(true));

  useEffect(() => {
    window.addEventListener('beforeunload', unloadListener);

    return () => {
      stopPlayback();
      window.removeEventListener('beforeunload', unloadListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recordWatchTime = useCallback(
    throttle((player: PlayerController) => {
      if (!player) {
        return;
      }
      const currentPlayerTime = Math.trunc(player.currentTime());
      if (previousPlayerTime.current !== currentPlayerTime) {
        watchDurationRef.current += 1;
        previousPlayerTime.current = currentPlayerTime;
      }
    }, THROTTLE_RECORD_WATCH_TIME),
    [],
  );

  const handleIsInteractingChange = useCallback(
    newIsInteracting => {
      if (newIsInteracting) {
        debounceToggleIsInteracting.cancel();
        toggleIsInteracting(true);
      } else {
        debounceToggleIsInteracting(false);
      }
    },
    [debounceToggleIsInteracting, toggleIsInteracting],
  );

  const togglePlayerAutoplay = useCallback((auto?: boolean) => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    const newAutoPlay = auto ?? !player.autoplay();
    player.autoplay(newAutoPlay && 'any');
  }, []);

  const togglePlayerMute = useCallback((mute?: boolean, shouldTrack = true) => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    if (player.isSsaiAdPlaying() && shouldTrack) {
      trackSsaiAdInteraction(mute ? AdEvents.AdMuted : AdEvents.AdUnmuted);
    }

    const newIsMuted = mute ?? !player.muted();
    player.muted(newIsMuted);
    setIsMuted(newIsMuted);

    if (!newIsMuted && player.volume() === 0) {
      player.volume(PLAYER_VOLUME_UNMUTE);
      setVolume(PLAYER_VOLUME_UNMUTE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPlayerVolume = useCallback((newVolume: number) => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    const adjustedNewVolume = numberIn0To1(Math.floor(newVolume * 100) / 100);
    player.volume(adjustedNewVolume);
    setVolume(adjustedNewVolume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVolumeUp = useCallback(() => {
    setPlayerVolume((volume || 0) + PLAYER_VOLUME_JUMP);
  }, [setPlayerVolume, volume]);

  const handelVolumeDown = useCallback(() => {
    setPlayerVolume((volume || 0) - PLAYER_VOLUME_JUMP);
  }, [setPlayerVolume, volume]);

  const handelVolumeChange = useCallback(
    (newVolume: number) => {
      if (newVolume === 0) {
        togglePlayerMute(true);
      } else {
        togglePlayerMute(false);
      }
      setPlayerVolume(newVolume);
    },
    [setPlayerVolume, togglePlayerMute],
  );

  const hidePoster = usePersistCallback(() => {
    posterVisible.current = false;
    playerBaseRef.current?.removeClass(POSTER_CLASSNAME);
    const player = playerRef.current;
    if (player) {
      togglePlayerMute(false, false);
    }
  });

  const getPlayerCurrentTime = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return 0;
    }
    return player.currentTime() - (isLivePlayer ? player.getLiveStartOffset() : 0);
  }, [isLivePlayer]);

  const refreshCurrentTime = useCallback(() => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    const playerCurrentTime = getPlayerCurrentTime();
    setCurrentTime(playerCurrentTime);
  }, [getPlayerCurrentTime]);

  const playerPlay = useCallback(async () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    await player.play();
    // When live resume the player, reset the progress bar to live current time
    refreshCurrentTime();

    setIsPlaying(true);

    if (player.isSsaiAdPlaying()) {
      trackSsaiAdInteraction(AdEvents.AdResumed);
    }
  }, [refreshCurrentTime, trackSsaiAdInteraction]);

  const playerPause = useCallback(() => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    player.pause();

    setIsPlaying(false);

    if (player.isSsaiAdPlaying()) {
      trackSsaiAdInteraction(AdEvents.AdPaused);
    }
  }, [trackSsaiAdInteraction]);

  const handleIsPlayingChange = useCallback(
    (newIsPlaying: boolean) => {
      if (newIsPlaying) {
        playerPlay();
      } else {
        playerPause();
      }
    },
    [playerPause, playerPlay],
  );

  const playerPlayTo = useCallback(
    newTime => {
      const player = playerRef.current;

      if (!player) {
        return;
      }
      if (isSeeking) return;

      let finalTime = newTime + (isLivePlayer ? player.getLiveStartOffset() : 0);
      if (!isLivePlayer && finalTime === 0) {
        // Resolves a bug in BC SSAI when seeking to 0, the player stalls then displays an error
        finalTime = SSAI_SEEK_ZERO_WORKAROUND_TIME;
      }

      player.currentTime(finalTime);
      setCurrentTime(isLivePlayer ? newTime : finalTime);

      onPlaybackPositionChange(player);
    },
    [onPlaybackPositionChange, isLivePlayer, isSeeking],
  );

  const playerPlayBack = useCallback(() => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    const playerCurrentTime = getPlayerCurrentTime();

    let newTime = playerCurrentTime - PLAYER_DURATION_JUMP;
    if (newTime < 0) {
      newTime = 0;
    }
    playerPlayTo(newTime);
  }, [getPlayerCurrentTime, playerPlayTo]);

  const playerSubTitle = (track?: any) => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    player.subTitle(track);
  };
  const playerPlayForward = useCallback(() => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    const playerCurrentTime = getPlayerCurrentTime();
    const videoDuration = isLivePlayer ? player.getLiveWindowDuration() : player.duration();

    let newTime = playerCurrentTime + PLAYER_DURATION_JUMP;
    if (newTime > videoDuration) {
      newTime = videoDuration;
    }
    playerPlayTo(newTime);
  }, [getPlayerCurrentTime, isLivePlayer, playerPlayTo]);

  const handleLiveTagClick = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    // If player is not live, then back to live position
    if (!isLive) {
      player.seekToLiveEdge();

      const videoTime = player.currentTime() - player.getLiveStartOffset();
      setCurrentTime(videoTime);

      onPlaybackPositionChange(player);
    }
  }, [isLive, onPlaybackPositionChange]);

  const onWatchFromStart = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    const exactStartTime = player.getLiveStartOffset();
    player.currentTime(exactStartTime);
    setIsLive(false);
    setCurrentTime(player.getLiveWindowDuration() - player.getLiveStartOffset());
    onPlaybackPositionChange(player);
  }, [onPlaybackPositionChange]);

  const toggleFullScreen = useCallback(
    (fullScreen?: boolean) => {
      if (fullScreen !== undefined) {
        toggleIsFullScreen(fullScreen);
      } else {
        toggleIsFullScreen();
      }
    },
    [toggleIsFullScreen],
  );

  const enterFullWindow = useCallback(() => {
    setIsFullWindow(true);
    isFullWindowRef.current = true;
    setShouldBodyScrollLock(true);
    // Set the page body to the same color as the player background,
    // to make phones with notch won't see the default blue color on sides
    document.body.classList.add(styles.bodyBackgroundColor);
  }, [setShouldBodyScrollLock]);

  const onExit = useCallback(() => {
    if (pauseOnExitFullWindow) {
      playerPause();
    }

    const player = playerRef.current;
    if (player) {
      onVideoEnd?.(player);
    }

    document.body.classList.remove(styles.bodyBackgroundColor);
    setShouldBodyScrollLock(false);
    const prevent = onExitFullWindow?.() === false;
    if (!prevent) {
      setIsFullWindow(false);
      isFullWindowRef.current = false;
    }
    stopPlayback();
  }, [onExitFullWindow, onVideoEnd, pauseOnExitFullWindow, playerPause, stopPlayback]);

  const handlePlayerPlaybackDataLoad = usePersistCallback(async () => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    posterVisible.current = true;
    if (isLivePlayer && !watchFromStart && IS_SAFARI && !player.isAtLiveEdge()) {
      // safari bug where playing live video with DVR window
      // starts at 0. Bug happens in Safari v18 but might be
      // gone in future versions
      player.seekToLiveEdge();
    }
    // When it is linear slot and watch from start then start playback from start offset
    if (playerMode === PLAYER_MODE.linearSlot && isLivePlayer && watchFromStart) {
      hidePoster();
      onWatchFromStart();
      // Set the start time after meta loaded, before start playing.
      // so that the player can directly buffer from the start time
    } else if (is(Number, startTime)) {
      onPlaybackDataLoad?.();
      if (!isLivePlayer) {
        if (typeof startTime === 'number' && startTime === 0) {
          hidePoster();
        }

        if (player.hasVmap()) {
          let finalStartTime = startTime ?? 0;
          ssaiAdPodCollectionRef.current = player.getAdPods(finalStartTime);

          if (playbackMetaRef.current && ssaiAdPodCollectionRef.current.length > 0) {
            playbackMetaRef.current = updateCuePoints(
              playbackMetaRef.current,
              ssaiAdPodCollectionRef.current,
            );
          }
          if (finalStartTime > 0) {
            // Find all ad pods behind finalStartTime and add their duration to finalStartTime
            finalStartTime += getAdTotalDurationBehindTime(
              ssaiAdPodCollectionRef.current,
              finalStartTime,
            );

            // if finalStartTime falls in between ad pod's start and end time,
            // add that ad pod's duration to finalStartTime
            finalStartTime +=
              ssaiAdPodCollectionRef.current.find(
                pod => pod.startTime <= finalStartTime && pod.endTime > finalStartTime,
              )?.duration ?? 0;
            player.seekInAbsoluteTime(finalStartTime);
          }
        } else {
          player.currentTime(startTime);
        }
      } else {
        hidePoster();
      }
    }
    hasPlayerPlaybackDataLoaded.current = true;
    hasVideoContentStartedFiredRef.current = false;
  });

  const handlePlayerFirstPlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }

    // Reset the volume back to default, in some case it may be muted by autoplay error
    setPlayerVolume(volume || 0);
  }, [setPlayerVolume, volume]);

  const handlePlayerVideoEnd = useCallback(async () => {
    setHasVideoEnded(true);
    if (!isConcurrencyLimitReached && playerRef.current && hasAnimationEndedRef.current) {
      onVideoEnd?.(playerRef.current);

      if (exitFullWindowOnPlayEnd && !hasAutoPlayOnEndRef.current) {
        onExitFullWindow?.();
      }
    }
    setIsPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConcurrencyLimitReached, onVideoEnd, exitFullWindowOnPlayEnd, onExitFullWindow]);

  // autoPlay changed
  useEffect(() => {
    autoPlayRef.current = autoPlay;
    if (playerRef.current) {
      togglePlayerAutoplay(autoPlay);
    }
  }, [autoPlay, togglePlayerAutoplay]);

  // fullWindow changed
  useEffect(() => {
    if (fullWindow) {
      enterFullWindow();
    } else {
      onExitFullWindow?.();
    }
  }, [enterFullWindow, onExitFullWindow, fullWindow]);

  // videoPlayback fetched, load it into player
  useEffect(() => {
    playbackMetaRef.current = playbackMeta;

    if (playbackMeta) {
      // Reset the state for the next video
      hasFirstPlayedRef.current = false;
      hasFirstTimeUpdatedRef.current = false;
      // setIsPlaying(false);
      setIsSeeking(false);
      setIsLive(false);
      setCurrentTime(0);
    }
  }, [playbackMeta]);

  useEffect(() => {
    // Only affect to VOD
    // Set duration here to show the duration on UI even before user start playing.
    // Live has `0`in playback response, will be re-calculated after start playing
    setDuration(mediaInfo.duration ?? 0); // duration in response is in milliseconds, player requires seconds
  }, [mediaInfo.duration]);

  /**
   * <------ Common Player events START ------>
   */

  const onPlayerPause = usePersistCallback(() => {
    if (playerRef.current) {
      onVideoPause?.(playerRef.current);
      setIsPlaying(false);
    }
  });

  const onPlayerPlay = usePersistCallback(() => {
    // Pause the playing Spotify when auto-play or resume.
    AppEventBus.emit(AppEvent.VideoPlayerPlayingChange, true);

    if (playerRef.current) {
      const { current: hasFirstPlayed } = hasFirstPlayedRef;
      if (hasFirstPlayed === true) {
        // only execute onVideoPlay if it's not the first play.
        onVideoPlay?.(playerRef.current);
      }
    }
    setIsPlaying(true);
  });

  const onPlayerTimeUpdate = usePersistCallback(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    // Ignore the 0 currentTime for live, live current time won't be 0.
    if (isLivePlayer && player.currentTime() <= 0) {
      return;
    }

    const { current: hasFirstTimeUpdated } = hasFirstTimeUpdatedRef;
    if (!hasFirstTimeUpdated) {
      hasFirstTimeUpdatedRef.current = true;

      handlePlayerFirstPlay();
      refreshCurrentTime();
    }

    if (isLivePlayer) {
      // For live stream only, set live window duration & update event
      const liveCurrentTime = player.getLiveCurrentTime();

      if (!lastSeekableEndRef.current || lastSeekableEndRef.current !== liveCurrentTime) {
        lastSeekableEndRef.current = liveCurrentTime;
        setDuration(player.getLiveWindowDuration());
        // For linear slot start is fixed and end is dynamic. Need to keep on updating the controls currect time
        if (playerMode === PLAYER_MODE.linearSlot) {
          const videoTime = player.currentTime() - player.getLiveStartOffset();
          setCurrentTime(videoTime);
        }
      }
      // When live set the live indicator status
      setIsLive(player.isAtLiveEdge());

      // For LIVE, wait for 30 seconds before firing VideoContentStarted
      if (watchDurationRef.current >= 30 && !hasVideoContentStartedFiredRef.current) {
        onVideoFirstPlay?.(player, {});
        hasVideoContentStartedFiredRef.current = true;
      }
    } else {
      refreshCurrentTime();

      // For VOD, if currently not playing a preroll, fire VideoContentStarted immediately
      // Otherwise, fire VideoContentStarted after PodEnded event in line 961
      if (!player.isSsaiPrerollPlaying() && !hasVideoContentStartedFiredRef.current) {
        onVideoFirstPlay?.(player, {});
        hasVideoContentStartedFiredRef.current = true;
      }
    }
    recordWatchTime(player);
    onTimeUpdate?.(player);
  });

  const onPlayerAdTimeUpdate = usePersistCallback(() => {
    const player = playerRef.current;
    if (!player || isLivePlayer || !ssaiAdPodPlayingRef.current) {
      return;
    }
    const { endTime } = ssaiAdPodPlayingRef.current;
    setAdCountdown(endTime - player.absoluteTime());
  });

  const onPlayerBuffering = usePersistCallback(() => {
    setBufferRanges(playerRef.current?.buffered() as TimeRanges);
  });

  const onPlayerSeeking = usePersistCallback(() => {
    setIsSeeking(true);
  });

  const onPlayerSeeked = usePersistCallback(() => {
    setIsSeeking(false);
    if (previousPlayerTime.current > 0 && posterVisible.current && playerBaseRef.current) {
      posterVisible.current = false;
      setTimeout(() => hidePoster(), HIDE_BC_POSTER_DELAY);
    }
  });

  const onPlayerError = usePersistCallback(() => {
    if (playbackMeta) playerRef.current?.reload?.(playbackMeta);
  });

  const onPlayerLoaded = usePersistCallback(
    (playerController: PlayerController, playerBase: any) => {
      playerBaseRef.current = playerBase;
      // The video tag
      playerRef.current = playerController;
      videoRef.current = playerController.videoEl;
      onPlayerLoad?.();

      // Set state initial values
      togglePlayerAutoplay(autoPlayRef.current);
      setPlayerVolume(volume || 0);
      if (!posterVisible.current) {
        togglePlayerMute(isMuted);
      }
    },
  );

  const onPlayerUnload = useCallback(() => {
    recordWatchTime.cancel();
    playerRef.current = undefined;
  }, [recordWatchTime]);

  // We'll keep these event handlers in case we'd like to wire some logic into it:
  const onPlayerWaiting = useCallback(() => {}, []);

  const onPlayerPlaying = usePersistCallback(() => {
    if (playerRef.current) {
      onVideoPlaying?.(playerRef.current);
    }
    setIsPlaying(true);
  });

  const onPlayerSsaiAds = usePersistCallback((event: any) => {
    const { player } = event.target;
    const { ssai } = player;
    if (!ssai().timeline_ || !playerRef.current) return;

    let adPodPayload = {};
    let adPayload = {};
    let adEventType;

    if (event.type === SsaiEventType.PodStarted) {
      adEventType = AdEvents.AdBlockStarted;
      setIsAdPlaying(true);

      // Grab ad pod currently played
      const podIndex = ssai().currentTimelineState().linearAdRoll.podIndex_;
      ssaiAdPodIndexRef.current = podIndex;
      ssaiAdPodPlayingRef.current = ssaiAdPodCollectionRef.current[podIndex];
      ssaiStrokeDashOffsetStepRef.current =
        MAX_STROKE_DASH_OFFSET / ssaiAdPodCollectionRef.current[podIndex].duration;

      adPodPayload = {
        ...adPodPayload,
        ad_block_id: ssaiAdPodPlayingRef.current.podId,
        ad_position: ssaiAdPodPlayingRef.current.type,
        total_ads_in_block: ssaiAdPodPlayingRef.current.adBreaks.length,
        total_block_duration: ssaiAdPodPlayingRef.current.duration,
      };
    }

    if (event.type === SsaiEventType.PodEnded) {
      adEventType = AdEvents.AdBlockCompleted;
      setIsAdPlaying(false);

      if (ssaiAdPodCollectionRef.current) {
        ssaiAdPodCollectionRef.current = ssaiAdPodCollectionRef.current.map(pod =>
          pod.index === ssaiAdPodIndexRef.current ? { ...pod, isWatched: true } : pod,
        );
      }
      // Store ad pod currently played
      if (ssaiAdPodPlayingRef.current) {
        adPodPayload = {
          ...adPodPayload,
          ad_block_id: ssaiAdPodPlayingRef.current.podId,
          ad_position: ssaiAdPodPlayingRef.current.type,
          total_ads_in_block: ssaiAdPodPlayingRef.current.adBreaks.length,
          total_block_duration: ssaiAdPodPlayingRef.current.duration,
        };
      }
    }

    if (event.type === SsaiEventType.AdStarted) {
      adEventType = AdEvents.AdImpression;

      // Grab ad currently played
      const adId = ssai().currentTimelineState().linearAd.id_;
      adPayload = ssaiAdPodPlayingRef.current?.adBreaks.find(ad => ad.adId === adId) ?? {};
      ssaiAdPlayingRef.current = adPayload as SsaiAdBreakProps;

      // Store ad pod currently played
      if (ssaiAdPodPlayingRef.current) {
        adPodPayload = {
          ...adPodPayload,
          ad_block_id: ssaiAdPodPlayingRef.current.podId,
          ad_position: ssaiAdPodPlayingRef.current.type,
          total_ads_in_block: ssaiAdPodPlayingRef.current.adBreaks.length,
          total_block_duration: ssaiAdPodPlayingRef.current.duration,
        };
      }
    }

    if (event.type === SsaiEventType.AdEnded) {
      adEventType = AdEvents.AdCompleted;

      // Store ad pod currently played
      if (ssaiAdPodPlayingRef.current) {
        adPodPayload = {
          ...adPodPayload,
          ad_block_id: ssaiAdPodPlayingRef.current.podId,
          ad_position: ssaiAdPodPlayingRef.current.type,
          total_ads_in_block: ssaiAdPodPlayingRef.current.adBreaks.length,
          total_block_duration: ssaiAdPodPlayingRef.current.duration,
        };
      }
      // Use store ad currently played
      adPayload = {
        ...(ssaiAdPlayingRef.current ?? {}),
        watched_duration: ssaiAdPlayingRef.current?.duration,
        completion_rate: 100,
        reason: Reasons.Completed,
      };
    }

    if (adEventType) {
      onTrackSsaiAds?.(adEventType, adPodPayload, adPayload);

      if (
        event.type === SsaiEventType.PodEnded &&
        ssaiAdPodPlayingRef.current?.type === 'preroll'
      ) {
        adPayload = {
          pre_roll_served: true,
          ad_block_id: ssaiAdPodPlayingRef.current.podId,
          ads_completed_count: ssaiAdPodPlayingRef.current.adBreaks.length,
        };
        // Fire VideoContentStarted after preroll ended
        onVideoFirstPlay?.(player, adPayload);
        hasVideoContentStartedFiredRef.current = true;
      }
    }
  });

  // <------ Common Player events END ------>

  const playerEventProps = {
    onPlayerLoaded,
    onPlayerUnload,
    onEnded: handlePlayerVideoEnd,
    onDataLoaded: handlePlayerPlaybackDataLoad,
    onPlay: onPlayerPlay,
    onPause: onPlayerPause,
    onWaiting: onPlayerWaiting,
    onPlaying: onPlayerPlaying,
    onSeeking: onPlayerSeeking,
    onSeeked: onPlayerSeeked,
    onTimeUpdate: onPlayerTimeUpdate,
    onAdTimeUpdate: onPlayerAdTimeUpdate,
    onError: onPlayerError,
    onSsaiAdsPlaying: onPlayerSsaiAds,
  };

  const history = useHistory();
  const showUpgradeSubscriptionModal = useSubscriptionModal();

  const handleDrawerContentSelection = useCallback(
    async (id, selectedTab, drawerItem: DrawerContentItem) => {
      if (!isNil(drawerItem.requiredSubscriptions) && !isEmpty(drawerItem.requiredSubscriptions)) {
        if (isPUJEnabled) {
          // TODO: Replace placeholder check with proper EpisodeId validation
          const methodType = episodeId ? VIEWING_METHODS.VOD : VIEWING_METHODS.LINEAR;
          const getMethods = episodeId ? getEpisodeViewingMethods : getLinearChannelViewingMethods;
          const viewingMethods = await getMethods(drawerItem?.id);
          const offerId = getOfferIdFromViewingMethod(viewingMethods, methodType);
          navigateToPackage({
            offerId,
            history,
            location,
            openModal,
          });
        } else {
          showUpgradeSubscriptionModal(
            drawerItem.requiredSubscriptions,
            drawerItem.analyticsParams && {
              ...drawerItem.analyticsParams,
              categoryName: selectedTab?.title,
              viewName: getViewPage(history),
            },
          );
          return;
        }
      }
      hasPlayerPlaybackDataLoaded.current = false;
      onDrawerContentSelect?.(id, selectedTab, drawerItem);
      destroyMediaTailorSession();
      setIsAdPlaying(false);
    },
    // removed getEpisodeViewingMethods and getLinearChannelViewingMethods from
    // the dependency array to avoid unnecessary re-renders on player time update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onDrawerContentSelect,
      showUpgradeSubscriptionModal,
      episodeId,
      history,
      openModal,
      isPUJEnabled,
      location,
    ],
  );

  const playNextEpisode = useCallback(
    id => {
      hasPlayerPlaybackDataLoaded.current = false;
      onAutoPlayNextEpisode(id);
    },
    [onAutoPlayNextEpisode],
  );
  return (
    <AspectRatioBox
      className={classnames(styles.videoPlayer, { [styles.fullWindow]: isFullWindow }, className)}
      ratio={isFullWindow ? AspectRatio.RatioFree : AspectRatio.Ratio16by9}
      ref={videoPlayerContainerRef}
      testid="video-player"
    >
      <div className={styles.videoContainer}>
        {shouldUseShakaPlayer ? (
          <ShakaPlayer
            className={styles.videoWrapper}
            autoPlay={autoPlay}
            playbackMeta={playbackMeta}
            setIsAdPlaying={setIsAdPlaying}
            youboraPlayerConfig={youboraPlayerConfiguration}
            {...playerEventProps}
          />
        ) : (
          <BrightCovePlayer
            className={classnames(styles.videoWrapper, { [styles.showAutoPlay]: showAutoPlay })}
            playbackMeta={playbackMeta}
            isLivePlayer={isLivePlayer}
            setIsAdPlaying={setIsAdPlaying}
            onBuffering={onPlayerBuffering}
            youboraPlayerConfig={youboraPlayerConfiguration}
            {...playerEventProps}
          />
        )}
        {!showAutoPlay && (
          <ControlPanel
            isLivePlayer={isLivePlayer}
            isLive={isLive}
            assetId={assetId ?? ''}
            onSubTitleSelection={playerSubTitle}
            isShow={!isPlaying || isInteracting}
            isSport={isSport}
            onIsInteractingChange={handleIsInteractingChange}
            mediaInfo={mediaInfo}
            drawerTabs={drawerTabs}
            drawerContentList={drawerContentList}
            isDrawerContentLoading={isDrawerContentLoading}
            selectedTabId={selectedTabId}
            selectedContentId={selectedContentId}
            onDrawerTabChange={onDrawerTabChange}
            onDrawerContentSelect={handleDrawerContentSelection}
            onDrawerOpen={onDrawerOpen}
            onBackButtonClick={() => {
              trackSsaiAdInteraction(AdEvents.AdExited);
              onExit();
            }}
            isShowBackButton={fullWindow}
            isSeeking={isSeeking}
            bufferRanges={bufferRanges}
            currentTime={currentTime}
            isAdPlaying={isAdPlaying}
            duration={duration}
            onCurrentTimeChanging={F}
            onCurrentTimeChanged={playerPlayTo}
            durationJump={PLAYER_DURATION_JUMP}
            isPlaying={isPlaying}
            onIsPlayingChange={handleIsPlayingChange}
            onPlayBack={playerPlayBack}
            onPlayForward={playerPlayForward}
            isMuted={!!isMuted}
            onIsMutedChange={togglePlayerMute}
            volume={volume || 0}
            onVolumeChange={handelVolumeChange}
            onVolumeUp={handleVolumeUp}
            onVolumeDown={handelVolumeDown}
            isShowResourceButton={isShowResourceButton}
            isFullScreen={isFullScreen}
            onFullScreenChange={toggleFullScreen}
            onLiveTagClick={handleLiveTagClick}
            playerReferrer={playerReferrer}
            playerMode={playerMode ?? ''}
            hasPlayerPlaybackDataLoaded={hasPlayerPlaybackDataLoaded.current}
            adCountdown={adCountdown}
          />
        )}
        {!shouldUseShakaPlayer && hasPlayerPlaybackDataLoaded.current && !isAdPlaying && (
          <SkipIntroPanel
            currentTime={currentTime}
            onCurrentTimeChanged={playerPlayTo}
            playBackMeta={playbackMeta}
            brandId={brandId}
            episodeId={episodeId}
            isControlPanalShow={!isPlaying || isInteracting}
            videoLength={duration}
            setHasAutoPlayOnEnd={setHasAutoPlayOnEnd}
            showAutoPlay={showAutoPlay}
            hasVideoEnded={hasVideoEnded}
            setShowAutoPlay={setShowAutoPlay}
            setHasVideoEnded={setHasVideoEnded}
            playNextEpisode={playNextEpisode}
            setAnimationStatus={setAnimationStatus}
          />
        )}

        {!shouldUseShakaPlayer && isAdPlaying && (
          <AdCountdownPanel
            adCountdownStart={ssaiAdPodPlayingRef.current?.duration}
            adCountdown={adCountdown}
            strokeDashOffsetStep={ssaiStrokeDashOffsetStepRef.current}
            image={mediaInfo.contentTileHorizontal}
          />
        )}
      </div>
    </AspectRatioBox>
  );
};

export default memo(VideoPlayer);
